import { useEffect, useState, useCallback, useRef } from "react";
import { chGetChecklist, chPatchChecklist, chSubirFotoInspeccion, chBorrarFotoInspeccion } from "@/lib/prevelis";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Camera, Trash2, Loader2, Check, CircleSlash, X as XIcon, CheckCircle2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const field = "w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none text-sm";

// Opciones por tipo de respuesta
const OPTS = {
  conformidad: [
    { v: "conforme", label: "Conforme", cls: "border-emerald-500 bg-emerald-500 text-white", off: "border-slate-300 text-slate-600 hover:bg-emerald-50", icon: Check },
    { v: "no_conforme", label: "No conforme", cls: "border-red-500 bg-red-500 text-white", off: "border-slate-300 text-slate-600 hover:bg-red-50", icon: XIcon },
    { v: "na", label: "N/A", cls: "border-slate-500 bg-slate-500 text-white", off: "border-slate-300 text-slate-500 hover:bg-slate-100", icon: CircleSlash },
  ],
  presencia: [
    { v: "si", label: "Sí", cls: "border-emerald-500 bg-emerald-500 text-white", off: "border-slate-300 text-slate-600 hover:bg-emerald-50", icon: Check },
    { v: "no", label: "No", cls: "border-red-500 bg-red-500 text-white", off: "border-slate-300 text-slate-600 hover:bg-red-50", icon: XIcon },
    { v: "na", label: "N/A", cls: "border-slate-500 bg-slate-500 text-white", off: "border-slate-300 text-slate-500 hover:bg-slate-100", icon: CircleSlash },
  ],
};

export default function SuizaChecklist({ inspeccion, onProgreso, onEstado }) {
  const iid = inspeccion.id;
  const [bloques, setBloques] = useState([]);
  const [respuestas, setRespuestas] = useState({});
  const [fotos, setFotos] = useState([]);
  const [progreso, setProgreso] = useState({ total: 0, respondidas: 0, pct: 0, no_conformes: 0 });
  const [estado, setEstado] = useState(inspeccion.estado);
  const [abiertos, setAbiertos] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadItem, setUploadItem] = useState("");
  const timers = useRef({});

  useEffect(() => {
    chGetChecklist(iid).then((d) => {
      setBloques(d.bloques || []); setRespuestas(d.respuestas || {}); setFotos(d.fotos || []);
      setProgreso(d.progreso || {}); setEstado(d.estado);
      const first = (d.bloques || [])[0];
      if (first) setAbiertos({ [first.id]: true });
    }).catch(() => toast.error("No se pudo cargar el checklist")).finally(() => setLoading(false));
  }, [iid]);

  const visible = useCallback((it) => {
    if (!it.cond) return true;
    return (respuestas[it.cond.item] || {}).valor === it.cond.eq;
  }, [respuestas]);

  const persist = useCallback(async (payload) => {
    setSaving(true);
    try {
      const r = await chPatchChecklist(iid, payload);
      setProgreso(r.progreso); setEstado(r.estado);
      onProgreso?.(r.progreso); onEstado?.(r.estado);
    } catch { toast.error("No se pudo guardar"); }
    finally { setSaving(false); }
  }, [iid, onProgreso, onEstado]);

  const setValor = (itemId, valor) => {
    setRespuestas((s) => ({ ...s, [itemId]: { ...(s[itemId] || {}), valor } }));
    persist({ respuestas: { [itemId]: { valor } } });
  };
  const setObs = (itemId, observacion) => {
    setRespuestas((s) => ({ ...s, [itemId]: { ...(s[itemId] || {}), observacion } }));
    clearTimeout(timers.current[itemId]);
    timers.current[itemId] = setTimeout(() => persist({ respuestas: { [itemId]: { observacion } } }), 700);
  };
  const marcarBloqueNA = (b) => {
    const patch = {};
    const next = { ...respuestas };
    b.items.forEach((it) => {
      if (it.tipo !== "datos" && visible(it)) { patch[it.id] = { valor: "na" }; next[it.id] = { ...(next[it.id] || {}), valor: "na" }; }
    });
    setRespuestas(next);
    if (Object.keys(patch).length) persist({ respuestas: patch });
  };
  const finalizar = () => persist({ estado: "finalizada" });

  const onUpload = async (itemId, fileList) => {
    if (!fileList?.length) return;
    setUploadItem(itemId);
    try {
      for (const f of fileList) {
        const foto = await chSubirFotoInspeccion(iid, f, itemId);
        setFotos((s) => [...s, foto]);
        setRespuestas((s) => ({ ...s, [itemId]: { ...(s[itemId] || {}), fotos: [...((s[itemId] || {}).fotos || []), foto.id] } }));
      }
      toast.success("Foto añadida");
    } catch { toast.error("No se pudo subir la foto"); }
    finally { setUploadItem(""); }
  };
  const borrarFoto = async (itemId, fid) => {
    try {
      await chBorrarFotoInspeccion(iid, fid);
      setFotos((s) => s.filter((x) => x.id !== fid));
      setRespuestas((s) => ({ ...s, [itemId]: { ...(s[itemId] || {}), fotos: ((s[itemId] || {}).fotos || []).filter((x) => x !== fid) } }));
    } catch { toast.error("No se pudo borrar"); }
  };

  const bloqueStats = (b) => {
    const vis = b.items.filter(visible);
    const resp = vis.filter((it) => { const v = (respuestas[it.id] || {}).valor; return v !== undefined && v !== null && v !== ""; });
    const nc = vis.filter((it) => { const v = (respuestas[it.id] || {}).valor; return (it.tipo === "conformidad" && v === "no_conforme") || (it.tipo === "presencia" && v === "no" && it.critico); });
    return { total: vis.length, resp: resp.length, nc: nc.length };
  };

  if (loading) return <div className="text-slate-400 text-sm flex items-center gap-2" data-testid="ch-checklist-loading"><Loader2 className="w-4 h-4 animate-spin" /> Cargando checklist…</div>;

  return (
    <div data-testid="ch-checklist">
      {/* Barra de progreso */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 sticky top-2 z-10">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-slate-800" data-testid="ch-checklist-progreso">{progreso.respondidas}/{progreso.total} respondidas · {progreso.pct}%</span>
            {progreso.no_conformes > 0 && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700" data-testid="ch-checklist-nc">{progreso.no_conformes} no conformidad(es)</span>}
            {saving ? <span className="text-xs text-slate-400 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Guardando…</span>
              : <span className="text-xs text-emerald-600 inline-flex items-center gap-1"><Check className="w-3 h-3" /> Guardado</span>}
          </div>
          <button onClick={finalizar} data-testid="ch-checklist-finalizar" disabled={estado === "finalizada"}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800 disabled:opacity-40">
            <CheckCircle2 className="w-4 h-4" /> {estado === "finalizada" ? "Checklist finalizado" : "Finalizar checklist"}
          </button>
        </div>
        <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-blue-600 transition-all" style={{ width: `${progreso.pct}%` }} />
        </div>
      </div>

      <div className="space-y-3">
        {bloques.map((b) => {
          const st = bloqueStats(b);
          const open = !!abiertos[b.id];
          return (
            <div key={b.id} data-testid={`ch-bloque-${b.id}`} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 p-4 cursor-pointer" onClick={() => setAbiertos((s) => ({ ...s, [b.id]: !s[b.id] }))} data-testid={`ch-bloque-toggle-${b.id}`}>
                {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                <span className="font-medium text-slate-900 flex-1">{b.orden}. {b.es}{b.sectorial && <span className="ml-2 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">Sectorial</span>}</span>
                {st.nc > 0 && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">{st.nc} NC</span>}
                <span className="text-xs text-slate-500">{st.resp}/{st.total}</span>
              </div>
              {open && (
                <div className="border-t border-slate-100 p-4 space-y-4">
                  <div className="flex justify-end">
                    <button onClick={(e) => { e.stopPropagation(); marcarBloqueNA(b); }} data-testid={`ch-bloque-na-${b.id}`}
                      className="text-xs font-medium text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"><CircleSlash className="w-3.5 h-3.5" /> Marcar bloque como N/A</button>
                  </div>
                  {b.items.filter(visible).map((it) => {
                    const r = respuestas[it.id] || {};
                    const itemFotos = fotos.filter((f) => (r.fotos || []).includes(f.id));
                    return (
                      <div key={it.id} data-testid={`ch-item-${it.id}`} className="pb-4 border-b border-slate-100 last:border-0 last:pb-0">
                        <div className="text-sm text-slate-800 mb-2">{it.es}{it.critico && <span className="ml-1 text-red-500" title="Punto crítico">*</span>}</div>
                        {it.tipo === "datos" ? (
                          <input data-testid={`ch-item-datos-${it.id}`} value={r.valor || ""} onChange={(e) => setRespuestas((s) => ({ ...s, [it.id]: { ...(s[it.id] || {}), valor: e.target.value } }))}
                            onBlur={(e) => persist({ respuestas: { [it.id]: { valor: e.target.value } } })} className={field} placeholder="Escribe el dato…" />
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {OPTS[it.tipo].map((o) => {
                              const active = r.valor === o.v;
                              const Icon = o.icon;
                              return (
                                <button key={o.v} onClick={() => setValor(it.id, o.v)} data-testid={`ch-item-${it.id}-${o.v}`}
                                  className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-sm font-medium transition-colors ${active ? o.cls : o.off}`}>
                                  <Icon className="w-3.5 h-3.5" /> {o.label}
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {it.tipo !== "datos" && (
                          <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_auto] items-start">
                            <textarea data-testid={`ch-item-obs-${it.id}`} rows={1} value={r.observacion || ""} onChange={(e) => setObs(it.id, e.target.value)}
                              className={field} placeholder="Observación (opcional)…" />
                            <label className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer shrink-0" data-testid={`ch-item-foto-${it.id}`}>
                              {uploadItem === it.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} Foto
                              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => onUpload(it.id, Array.from(e.target.files))} />
                            </label>
                          </div>
                        )}
                        {itemFotos.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {itemFotos.map((f) => (
                              <div key={f.id} className="relative w-16 h-16 rounded-lg overflow-hidden border border-slate-200" data-testid={`ch-item-foto-thumb-${f.id}`}>
                                <img src={`${BACKEND}${f.url}`} alt="" className="w-full h-full object-cover" />
                                <button onClick={() => borrarFoto(it.id, f.id)} className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center"><Trash2 className="w-3 h-3" /></button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
